import type { SplitIds } from '@/clientOnlyUtils/clientUtils';
import { joinIds } from '@/clientOnlyUtils/clientUtils';
import { type ComponentProps } from 'react';

import { cn } from '@poseidon/components/cn';
import { ContactCardImageWithOwner } from '@poseidon/components/ContactCardImage';
import ContactCardNameRowPure from '@poseidon/components/ContactCardNameRowPure';
import ContactCardOrgRowPure from '@poseidon/components/ContactCardOrgRowPure';
import { contactName } from '@poseidon/components/contactUtils';
import { MessageBody } from '@poseidon/components/MessageBody';
import { shouldShowHonorific } from '@poseidon/shared-utils';

import { useRouterQuery } from '@/hooks/useRouterQuery';
import { CheckboxIconButton } from '@poseidon/components/Input2';
import { ContactCardContainer } from '@poseidon/components/ContactCardContainer';

type LocalCadence = {
  id: string;
  name?: string;
  isActive?: boolean;
};

type LocalContact = {
  id: string;
  primaryPictureUrl?: string | null;
  liOpenLink?: boolean | null;
  notesPreview?: string | null;
  primaryFields: {
    firstName?: { data?: string | null } | null;
    lastName?: { data?: string | null } | null;
    nameFull?: { data?: string | null } | null;
    name?: { data?: string | null } | null;
    title?: { data?: string | null } | null;
    honorificTitle?: { data?: string | null } | null;
  };
};

type LocalContactUser = {
  profilePicture?: string | null;
  liConnected?: boolean | null;
  liConnectedAt?: Date | null;
  name?: string | null;
  email?: string | null;
  disqualified?: boolean;
};

type LocalInteraction = {
  id: string;
  interactionAction: string;
  body: string;
};

type LocalOrg = {
  primaryPictureUrl?: string | null;
  primaryFields?: {
    name?: { data?: string | null } | null;
  };
};

export type ResponseCardProps = ComponentProps<'div'> & {
  cadence?: LocalCadence | null;
  contact: LocalContact;
  contactUser: LocalContactUser;
  authedUser?: LocalContactUser;
  response: LocalInteraction;
  org: LocalOrg | null;
  onToggleChecked?: (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const ResponseCard = ({
  contact,
  cadence,
  contactUser,
  authedUser,
  response,
  org,
  onToggleChecked,
  className,
}: ResponseCardProps) => {
  const [query, setQuery] = useRouterQuery();
  const isChecked =
    onToggleChecked != null &&
    query.selectedIds?.includes(joinIds({ interactionId: response.id, cadenceId: cadence?.id, contactId: contact.id }));
  const somethingChecked = (query.selectedIds?.length || 0) > 0;

  const onClick = () => {
    setQuery((o) => ({
      ...o,
      scheduledStepId: undefined,
      id: contact.id,
      cadenceId: undefined,
      orgId: undefined,
      stepId: undefined,
    }));
  };

  return (
    <div className={cn('group flex gap-3.5', className)}>
      {onToggleChecked && (
        <div>
          <CheckboxIconButton
            onClick={(e) => onToggleChecked({ interactionId: response.id, contactId: contact.id, cadenceId: cadence?.id }, e)}
            checked={!!isChecked}
            somethingChecked={somethingChecked}
          />
        </div>
      )}

      <ContactCardContainer isChecked={isChecked} active={query.id === contact.id} onClick={onClick}>
        <ContactCardImageWithOwner
          name={contactName(contact)}
          firstName={contact.primaryFields?.firstName?.data}
          lastName={contact.primaryFields?.lastName?.data}
          imageSrc={contact.primaryPictureUrl}
          orgPictureUrl={org?.primaryPictureUrl}
          cadenceName={cadence?.name}
          showContactUser={contactUser != null}
          contactUserProfilePicture={contactUser?.profilePicture}
          contactUserLiConnected={contactUser?.liConnected}
          contactUserLiConnectedAt={contactUser?.liConnectedAt}
          contactUserName={contactUser?.name}
          contactUserEmail={contactUser?.email}
          authedUserLiConnected={authedUser?.liConnected}
          authedUserLiConnectedAt={authedUser?.liConnectedAt}
        />
        <ResponseCardBody
          contact={contact}
          cadence={cadence}
          org={org}
          response={response}
          contactUser={contactUser}
          authedUser={authedUser}
        />
      </ContactCardContainer>
    </div>
  );
};

export default function ResponseCardBody({
  cadence,
  contact,
  contactUser,
  authedUser,
  response,
  org,
  className,
}: ResponseCardProps) {
  const title = contact.primaryFields.title?.data;
  const rawHonorific = contact.primaryFields?.honorificTitle?.data ?? null;
  const honorificTitle = shouldShowHonorific(rawHonorific, undefined, undefined) ? rawHonorific : undefined;

  return (
    <div className={cn('flex w-0 shrink grow flex-col justify-between text-slate-800 dark:text-white', className)}>
      <div className="flex justify-between gap-1.5 overflow-hidden">
        <div className="flex w-0 grow flex-col gap-1.5">
          <ContactCardNameRowPure
            name={contactName(contact)}
            honorificTitle={honorificTitle}
            liOpenLink={contact.liOpenLink}
            liConnected={contactUser?.liConnected ?? authedUser?.liConnected}
            cadenceName={cadence?.name}
            cadenceIsActive={cadence?.isActive}
            disqualified={contactUser?.disqualified ?? authedUser?.disqualified}
            notesPreview={contact.notesPreview}
          />
          <ContactCardOrgRowPure
            orgName={org?.primaryFields?.name?.data ?? null}
            orgPictureUrl={org?.primaryPictureUrl ?? null}
            title={title ?? undefined}
          />
        </div>
      </div>

      <div className="flex min-h-4.5 gap-0.5 overflow-hidden text-xs text-charade-700 dark:text-charade-200">
        <div className="w-full overflow-hidden">
          {response.interactionAction == 'email_received' && <div className="truncate">Email received</div>}
          {response.interactionAction != 'email_received' && (
            <div className="truncate">
              <MessageBody showContactErrors html={response.body} firstLineOnly />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
